import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActionSheetController } from '@ionic/angular';

import { platformService } from '@app/modules/common/platform'; 
import { alertService } from '@app/modules/common/alert';
import { languageService, languageSupport } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { Place } from '@app/modules/model/place';
import { Offer } from '@app/modules/model/offer';
import { Discount } from '@app/modules/model/discount';
import { User } from '@app/modules/model/user';
import { DiscountPeriod, OfferPeriod } from '@app/modules/model/period';
import { OfferProduct } from '@app/modules/model/offerproduct';

/************************************/
/* OFFER COMPONENT                  */
/************************************/

@Component({
    selector: 'ui-offer-edit',
    templateUrl: 'ui-edit.html',
    styleUrls: [
      'ui-edit.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiOfferEdit extends languageSupport implements OnInit, OnChanges, OnDestroy {    
    @Input('place') _place: Place = null; 
    @Input('offer') _item: Offer | Discount = null;
    @Input('user') _user: User = null; 

    private _selectedproducts = null;
    get SelectProducts(){
        return this._selectedproducts;
    }

    private get _targettype() : 'offer' | 'menu' | 'dscnt' {
        if (this._offer){
            return 'offer';
        }

        if (this._menu){
            return 'menu';
        }

        if (this._discount){
            return 'dscnt';
        }

        return null;
    }

    get TargetForm(){
        switch(this._targettype){
            case 'offer': 
                return this.OfferForm;
            case 'menu': 
                return this.MenuForm;
            case 'dscnt': 
                return this.DiscountForm;
        }

        return null;
    }

    private LoadTargetForm(){
        switch(this._targettype){
            case 'offer':
                this.LoadOfferForm();
                break;
            case 'menu': 
                this.LoadMenuForm();
                break;
            case 'dscnt':
                this.LoadDiscountForm();
                break;
        }
    }
    
    get _offer(){
        let _offer: Offer = (this._item instanceof Offer) ? (this._item as Offer) : null;
        if (_offer){
            return _offer.ismenu ? null : _offer;
        }
        return null;
    }

    get _menu(){
        let _menu: Offer = (this._item instanceof Offer) ? (this._item as Offer) : null;
        if (_menu){
            return _menu.ismenu ? _menu : null;
        }
        return null;
    }

    get _discount(){
        let _discount: Discount = (this._item instanceof Discount) ? (this._item as Discount) : null;
        if (_discount){
            return _discount;
        }
        return null;
    }

    get CanModify(){
        return this._place.AllowOfferManage(this._user);
    }

    OnShowProducts(_products = []){
        this._selectedproducts = _products;
        this.change.markForCheck();
    }

    OnHideProducts(){
        this._selectedproducts = null;
        this.change.markForCheck();
    }

    get CanRemove(){
        if (this.CanModify){
            return (this._item && this._item.IsValid && !this._item.ToInsert);
        }
        return false;
    }

    private get _ActivePeriods(){
        let _periods = [];
        for(let _period of this._item.periods){
            if (_period.IsValid){
                _periods.push(_period);
            }
        }
        return _periods;
    }
    
    private ValidatePeriods(control: FormControl) {
        let _isvalid = control.value.every(period => {
            return period.IsValid;
        })

        return _isvalid ? null : {'period': {valid: false}};
    }

    OnAllDayChange(){
        let _control = this.TargetForm.get('periods');
        if (_control){
            if (this.TargetForm.get('allday').value){
                _control.clearValidators();
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }
            else {
                _control.setValidators([ Validators.required, this.ValidatePeriods.bind(this) ]);
                _control.setErrors(null);
                _control.updateValueAndValidity();
            }            
        }
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();     
    private OnSelect(_item){
        this._onPanel.emit({
            scnd: 'offer',
            args: {
                offer: _item
            }
        });
    }

    private _nophoto = '/assets/image/offer.png';
 
    /**********************************/
    /* OFFER VIEW                     */
    /**********************************/

    OnCreateOffer(){
        let _offer = new Offer(null, this.data);
        if (_offer){
            _offer.ismenu = false;
            _offer.place = this._place;
            _offer.status = 'AC';
        }

        this.OnSelect(_offer);
    }

    private _offerForm = null;   
    private get OfferForm(){
        if (this._offerForm == null){
            this.LoadOfferForm();
        }
        return this._offerForm;
    }

    private get _OfferPeriods(){
        let _periods = [];
        for(let _period of this._ActivePeriods){
            _periods.push(_period.Copy());
        }
        return _periods;
    }

    private get _OfferProducts(){
        let _grouped = {};
        for(let _product of this._offer.products){
            if (_product.IsValid){
                if (!(_product.grouped in _grouped)){
                    _grouped[_product.grouped] = [];
                }
                _grouped[_product.grouped].push(_product.Copy());    
            }
        }

        let _products = [];
        for(let _group in _grouped){
            _products.push(_grouped[_group]);
        }
        return _products;
    }   

    private LoadOfferForm(){
        let _photo = this._offer.avatar || this._nophoto;
        let _name = this._offer.name;
        let _price = this._offer.price;
        let _description = this._offer.description
        let _periods = this._OfferPeriods;      // make a copy of the offer periods
        let _products = this._OfferProducts;    // make a copy of the offer products
        let _allday = this._offer.allday;

        if (this._offerForm == null){
            this._offerForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                price: new FormControl(_price, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                allday: new FormControl(_allday, [
                    // no validators
                ]),
                periods: new FormControl(_periods, [
                    // no validators
                ]),
                products: new FormControl(_products, [
                    Validators.required
                ])
            });
        }
        else {
            this._offerForm.patchValue({
                photo: _photo,
                name: _name,
                price: _price,
                description: _description,
                allday: _allday,
                periods: _periods,
                products: _products
            });
        }

        this.OnAllDayChange();

        let _alldaycontrol = this._offerForm.get('allday');
        if (_alldaycontrol){
            this.CanModify ? _alldaycontrol.enable() : _alldaycontrol.disable();
        }

        this._offerForm.markAsPristine();
        setTimeout(() => {
            this._offerForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    private _ApplyOfferPeriods(_offer, periods){
        let _periods = [];

        for(let _period of periods){
            _period.offer = _offer;

            let _copyof = _period.CopyOf;               
            if (_copyof){
                _copyof.offer = _period.offer;
                _copyof.ini = _period.ini;
                _copyof.end = _period.end;
                _copyof.weekdays = _period.weekdays;
                _copyof.timezone = _period.timezone;

                _periods.push(_copyof);
            }
            else {
                _periods.push(_period);
            }
        }

        // include all periods in the final list
        for(let _period of _periods){
            _offer.AddPeriod(_period);
        }

        // remove periods not in the final list
        for (let _period of _offer.periods){
            if (_periods.indexOf(_period) == -1){
                _period.status = 'DE';
            }            
        }
    }

    private _ApplyOfferProducts(_offer, _grouped){
        let _offerproducts: Array <OfferProduct> = [];

        // update the offerproduct information (offer and group)
        for(let _idx=0; _idx < _grouped.length; _idx++){
            for(let _offerproduct of _grouped[_idx]){
                _offerproduct.offer = _offer;
                _offerproduct.grouped = _idx;
                _offerproducts.push(_offerproduct);
            }
        }

        // include all products in the final list
        for(let _offerproduct of _offerproducts){
            _offer.AddProduct(_offerproduct);
        }

        // remove products not in the final list
        for(let _offerproduct of _offer.products){
            if (!_offerproducts.includes(_offerproduct)){
                _offerproduct.status = 'DE';
            }
        }

        // refresh the products view
        for(let _offerproduct of _offer.products){
            _offerproduct.product.DoRefresh('OFFER');
        }
    }

    private async _CommitOffer(_offer){
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            if (_offer){
                this._offer.status = 'DE';
                if (this._offer.ToUpdate){
                    _transaction.SendCommit(this._offer);
                }

                _offer.CopyOf = null;
                _transaction.SendCommit(_offer);
                
                this._place.AddOffer(_offer);
            }
            
            return await _transaction.Flush(true);
        }

        return false;
    }

    async OnOfferAccept(){
        let _copy = this._offer.Copy() as Offer;
        if (_copy){
            _copy.photo = this.OfferForm.get('photo').value;
            if ((!_copy.hasPhoto) || (_copy.photo == this._nophoto)) {
                _copy.photo = _copy.base64 = null;
            }
    
            _copy.name = this.OfferForm.get('name').value;
            _copy.price = this.OfferForm.get('price').value;
            _copy.description = this.OfferForm.get('description').value;
            _copy.ismenu = false;   // hardcoded: as it has already been chosen
            _copy.allday = this.OfferForm.get('allday').value;
    
            this._ApplyOfferPeriods(_copy, this.OfferForm.get('periods').value);
            this._ApplyOfferProducts(_copy, this.OfferForm.get('products').value)
        }

        this.OfferForm.markAsPristine();

        if (await this._CommitOffer(_copy)){
            this.OnClose();
        }
    }

    /**********************************/
    /* MENU VIEW                      */
    /**********************************/

    OnCreateMenu(){
        let _menu = new Offer(null, this.data);
        if (_menu){
            _menu.ismenu = true;
            _menu.place = this._place;
            _menu.status = 'AC';
        }

        this.OnSelect(_menu);
    }

    private _menuForm = null;   
    private get MenuForm(){
        if (this._menuForm == null){
            this.LoadMenuForm();
        }
        return this._menuForm;
    }

    private get _MenuPeriods(){
        let _periods = [];
        for(let _period of this._ActivePeriods){
            _periods.push(_period.Copy());
        }
        return _periods;
    }

    private get _MenuProducts(){
        let _grouped = {};
        for(let _product of this._menu.products){
            if (_product.IsValid){
                if (!(_product.grouped in _grouped)){
                    _grouped[_product.grouped] = [];
                }
                _grouped[_product.grouped].push(_product.Copy());    
            }
        }

        let _products = [];
        for(let _group in _grouped){
            _products.push(_grouped[_group]);
        }
        return _products;
    }   

    private LoadMenuForm(){
        let _photo = this._menu.avatar || this._nophoto;
        let _name = this._menu.name;
        let _price = this._menu.price;
        let _description = this._menu.description
        let _periods = this._MenuPeriods;      // make a copy of the menu periods
        let _products = this._MenuProducts;    // make a copy of the menu products
        let _allday = this._menu.allday;

        if (this._menuForm == null){
            this._menuForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                price: new FormControl(_price, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                allday: new FormControl(_allday, [
                    // no validators
                ]),
                periods: new FormControl(_periods, [
                    // no validators
                ]),
                products: new FormControl(_products, [
                    Validators.required
                ])
            });
        }
        else {
            this._menuForm.patchValue({
                photo: _photo,
                name: _name,
                price: _price,
                description: _description,
                allday: _allday,
                periods: _periods,
                products: _products
            });
        }

        this.OnAllDayChange();

        let _alldaycontrol = this._menuForm.get('allday');
        if (_alldaycontrol){
            this.CanModify ? _alldaycontrol.enable() : _alldaycontrol.disable();
        }

        this._menuForm.markAsPristine();
        setTimeout(() => {
            this._menuForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    private _ApplyMenuPeriods(_menu, periods){
        let _periods = [];

        for(let _period of periods){
            _period.offer = _menu;

            let _copyof = _period.CopyOf;               
            if (_copyof){
                _copyof.offer = _period.offer;
                _copyof.ini = _period.ini;
                _copyof.end = _period.end;
                _copyof.weekdays = _period.weekdays;
                _copyof.timezone = _period.timezone;

                _periods.push(_copyof);
            }
            else {
                _periods.push(_period);
            }
        }

        // include all periods in the final list
        for(let _period of _periods){
            _menu.AddPeriod(_period);
        }

        // remove periods not in the final list
        for (let _period of _menu.periods){
            if (_periods.indexOf(_period) == -1){
                _period.status = 'DE';
            }            
        }
    }

    private _ApplyMenuProducts(_menu, _grouped){
        let _offerproducts: Array <OfferProduct> = [];

        // update the offerproduct information (offer and group)
        for(let _idx=0; _idx < _grouped.length; _idx++){
            for(let _offerproduct of _grouped[_idx]){
                _offerproduct.offer = _menu;
                _offerproduct.grouped = _idx;
                _offerproducts.push(_offerproduct);
            }
        }

        // include all products in the final list
        for(let _offerproduct of _offerproducts){
            _menu.AddProduct(_offerproduct);
        }

        // remove products not in the final list
        for(let _offerproduct of _menu.products){
            if (!_offerproducts.includes(_offerproduct)){
                _offerproduct.status = 'DE';
            }
        }

        // refresh the products view
        for(let _offerproduct of _menu.products){
            _offerproduct.product.DoRefresh('OFFER');
        }
    }

    private async _CommitMenu(_menu){
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            if (_menu){
                this._menu.status = 'DE';
                if (this._menu.ToUpdate){
                    _transaction.SendCommit(this._menu);
                }

                _menu.CopyOf = null;
                _transaction.SendCommit(_menu);
                
                this._place.AddOffer(_menu);
            }
            
            return await _transaction.Flush(true);
        }

        return false;
    }

    async OnMenuAccept(){
        let _copy = this._menu.Copy() as Offer;
        if (_copy){
            _copy.photo = this.MenuForm.get('photo').value;
            if ((!_copy.hasPhoto) || (_copy.photo == this._nophoto)) {
                _copy.photo = _copy.base64 = null;
            }
    
            _copy.name = this.MenuForm.get('name').value;
            _copy.price = this.MenuForm.get('price').value;
            _copy.description = this.MenuForm.get('description').value;
            _copy.ismenu = true;    // hardcoded: as it has already been chosen
            _copy.allday = this.MenuForm.get('allday').value;
    
            this._ApplyMenuPeriods(_copy, this.MenuForm.get('periods').value);
            this._ApplyMenuProducts(_copy, this.MenuForm.get('products').value)
        }

        this.MenuForm.markAsPristine();

        if (await this._CommitMenu(_copy)){
            this.OnClose();
        }
    }

    /**********************************/
    /* DISCOUNT VIEW                  */
    /**********************************/

    OnCreateDiscount(){
        let _discount = new Discount(null, this.data);
        if (_discount){
            _discount.place = this._place;
            _discount.status = 'AC';
        }

        this.OnSelect(_discount);
    }

    private _discountForm = null;   
    private get DiscountForm(){
        if (this._discountForm == null){
            this.LoadDiscountForm();
        }
        return this._discountForm;
    }

    private get _DiscountPeriods(){
        let _periods = [];
        for(let _period of this._ActivePeriods){
            _periods.push(_period.Copy());
        }
        return _periods;
    }

    private LoadDiscountForm(){
        let _photo = this._discount.avatar || this._nophoto;
        let _name = this._discount.name;
        let _type = this._discount.type;
        let _value = this._discount.value;
        let _description = this._discount.description
        let _periods = this._DiscountPeriods;      // make a copy of the discount periods
        let _allday = this._discount.allday;
        let _cumulative = this._discount.cumulative;

        if (this._discountForm == null){
            this._discountForm = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                name: new FormControl(_name, [ 
                    Validators.required 
                ]),
                type: new FormControl(_type, [ 
                    Validators.required 
                ]),
                value: new FormControl(_value, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                cumulative: new FormControl(_cumulative, [
                    // no validators
                ]), 
                allday: new FormControl(_allday, [
                    // no validators
                ]),
                periods: new FormControl(_periods, [
                    // no validators
                ])
            });
        }
        else {
            this._discountForm.patchValue({
                photo: _photo,
                name: _name,
                type: _type,
                value: _value,
                description: _description,
                cumulative: _cumulative,
                allday: _allday,
                periods: _periods,
            });
        }

        this.OnAllDayChange();

        let _alldaycontrol = this._discountForm.get('allday');
        if (_alldaycontrol){
            this.CanModify ? _alldaycontrol.enable() : _alldaycontrol.disable();
        }

        this._discountForm.markAsPristine();
        setTimeout(() => {
            this._discountForm.markAsPristine();
        }, 0);

        this.change.markForCheck();        
    }

    private _ApplyDiscountPeriods(_discount, periods){
        let _periods = [];

        for(let _period of periods){
            _period.discount = _discount;

            let _copyof = _period.CopyOf;               
            if (_copyof){
                _copyof.discount = _period.discount;
                _copyof.ini = _period.ini;
                _copyof.end = _period.end;
                _copyof.weekdays = _period.weekdays;
                _copyof.timezone = _period.timezone;

                _periods.push(_copyof);
            }
            else {
                _periods.push(_period);
            }
        }

        // include all periods in the final list
        for(let _period of _periods){
            _discount.AddPeriod(_period);
        }

        // remove periods not in the final list
        for (let _period of _discount.periods){
            if (_periods.indexOf(_period) == -1){
                _period.status = 'DE';
            }            
        }
    }

    private async _CommitDiscount(_discount){
        let _transaction = await this.data.BeginTransaction();
        if (_transaction){
            if (_discount){
                this._discount.status = 'DE';
                if (this._discount.ToUpdate){
                    _transaction.SendCommit(this._discount);
                }

                _discount.CopyOf = null;
                _transaction.SendCommit(_discount);
                
                this._place.AddDiscount(_discount);
            }
            
            return await _transaction.Flush(true);
        }

        return false;
    }

    async OnDiscountAccept(){
        let _copy = this._discount.Copy() as Discount;
        if (_copy){
            _copy.photo = this.DiscountForm.get('photo').value;
            if ((!_copy.hasPhoto) || (_copy.photo == this._nophoto)) {
                _copy.photo = _copy.base64 = null;
            }
    
            _copy.name = this.DiscountForm.get('name').value;
            _copy.type = this.DiscountForm.get('type').value;
            _copy.value = this.DiscountForm.get('value').value;
            _copy.description = this.DiscountForm.get('description').value;
            _copy.cumulative = this.DiscountForm.get('cumulative').value;
            _copy.allday = this.DiscountForm.get('allday').value;
    
            this._ApplyDiscountPeriods(_copy, this.DiscountForm.get('periods').value);
        }

        this.DiscountForm.markAsPristine();

        if (await this._CommitDiscount(_copy)){
            this.OnClose();
        }
    }

    /**********************************/
    /* STARTS HERE                    */
    /**********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, public platform: platformService, private actions: ActionSheetController, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _CanClose(){
        return new Promise(async (resolve) => {
            let _form = this.TargetForm;            
            if (_form && _form.dirty){
                console.log("[EDIT OFFER] confirm changes lost");
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@changes_lost_confirm_tittle'),
                    message: this.lang.tr('@changes_lost_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
    
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
    
                            return false;
                        }
                    }]
                });
    
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data){
                        _form.markAsPristine();
                    }
                    
                    resolve(detail.data);
                });
    
                await alert.present();        
            }
            else {
                resolve(true);
            }
        });
    }

    private _user_subscription = null;
    private _close_subscription = null;

    ngOnInit(){
        this._user_subscription = this._user.OnRefresh.subscribe(
        data => {
            this.LoadTargetForm();
        });

        this._close_subscription = this.view.OnCloseRequest.subscribe(
        async () => {
            this.view.OnCloseResponse(await this._CanClose());
        });
    }

    private _subscriptions = [];
    ngOnChanges(){
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        this.LoadTargetForm();

        if (this._item){
            this._subscriptions.push(
                this._item.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            )
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    private _OnClose(){
        if (this._close_subscription){
            this._close_subscription.unsubscribe();
            this._close_subscription = null;
        }

        if (this.view.RightPanel){
            this._onPanel.emit({
                scnd: null,
                args: null
            });    
        }
        else {
            this._onPanel.emit({
                scnd: null
            });

            // wait for animation to finish
            setTimeout(() => {
                this._onPanel.emit({
                    args: null
                });    
            }, 300);
        }
    }

    /**********************************/
    /* EVENT HANDLERS                 */
    /**********************************/

    OnPriceChange(){
        let _isdiscount = (this._targettype == 'dscnt');
        if (_isdiscount){
            let _control = this.TargetForm.get('value');

            let _value = _control.value;
            if (_value){
                if (this.TargetForm.get('type') == 'TICKETPRCNT'){
                    _control.setValue(Math.round(parseFloat(_value) || 0));
                }
                else {
                    _control.setValue(parseFloat(_value.toString().replace(",", ".")).toFixed(2));            
                }    
            }
            else {
                _control.setValue(null);
            }
        }
        else {
            let _control = this.TargetForm.get('price');

            let _value = _control.value;
            if (_value){
                _control.setValue(parseFloat(_value.toString().replace(",", ".")).toFixed(2));            
            }
            else {
                _control.setValue(null);
            }

        }
    }

    OnPeriodChange(valid){
        this.TargetForm.get('periods').setErrors(valid ? null : { 'periods': true });
        if (valid){
            this.TargetForm.markAsDirty();
        }
    }

    OnAddPeriod(){
        let _periods = this.TargetForm.get('periods').value;

        if (this._offer != null){
            let _period = new OfferPeriod(null, this.data);
            if (_period){
                _period.offer = this._offer;
                _periods.push(_period);
            }    
        }

        if (this._menu != null){
            let _period = new OfferPeriod(null, this.data);
            if (_period){
                _period.offer = this._menu;
                _periods.push(_period);
            }
        }

        if (this._discount != null){
            let _period = new DiscountPeriod(null, this.data);
            if (_period){
                _period.discount = this._discount;
                _periods.push(_period);
            }                
        }

        this.TargetForm.get('periods').setValue(_periods);
        this.TargetForm.markAsDirty();

        this.change.markForCheck();
    }

    OnDelPeriod(period){
        let _periods = this.TargetForm.get('periods').value;
        let _idx = _periods.indexOf(period);
        if (_idx != -1){
            _periods.splice(_idx, 1);
        }

        this.TargetForm.get('periods').setValue(_periods);
        this.TargetForm.markAsDirty();

        this.change.markForCheck();
    }

    OnSetProducts(products){
        this.OnShowProducts(products);
    }

    OnAddProducts(products){
        this.OnHideProducts();
        
        let _products = this.TargetForm.get('products').value;
        let _idx = _products.indexOf(products);
        if (_idx != -1){    // update products group
            _products.splice(_idx, 1, products);
        }
        else {              // insert products group
            _products.push(products);
        }

        this.TargetForm.get('products').setValue(_products);
        this.TargetForm.markAsDirty();

        this.change.markForCheck();
    }

    OnDelProducts(products){
        let _products = this.TargetForm.get('products').value;
        let _idx = _products.indexOf(products);
        if (_idx != -1){
            _products.splice(_idx, 1);
        }

        this.TargetForm.get('products').setValue(_products);
        this.TargetForm.markAsDirty();

        this.change.markForCheck();
    }

    private _menuoptions = {};
    MenuOptions(i){
        if (i === null){
            return Object.values(this._menuoptions).some(
            value => {
                return value;
            });
        }

        if (i in this._menuoptions){
            return this._menuoptions[i];
        }
        return false;
    }

    OnMenuOptions(i){
        if (i === null){
            for(let _idx in this._menuoptions){
                this._menuoptions[_idx] = false;
            }
        }
        else {
            this._menuoptions[i] = !this._menuoptions[i];
        }

        this.change.markForCheck();
    }

    /* TODO: revisar desde aqui */

    async OnAccept(){
        switch(this._targettype){
            case 'offer': 
                return this.OnOfferAccept();
            case 'menu':
                return this.OnMenuAccept();
            case 'dscnt':
                return this.OnDiscountAccept();
        }
    }

    async OnRemove(){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_offer_delete_confirm'),
            message: this.lang.tr('@place_offer_delete_message', [ this._item.name ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._item.status = 'DE';
                this._item.DoCommit();

                this.TargetForm.markAsPristine();
                this.OnClose();
            }
        });

        await alert.present();
    }

    async OnClose(){
        if (this.TargetForm && this.TargetForm.dirty){
            console.log("[EDIT OFFER] confirm changes lost");
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@changes_lost_confirm_tittle'),
                message: this.lang.tr('@changes_lost_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);

                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);

                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose();
                }
            });

            await alert.present();        
        }
        else {
            this._OnClose();
        }
    }
}